import React, { FC, useCallback, useRef, useState } from 'react';
import { NativeScrollEvent, NativeSyntheticEvent, StyleSheet, View } from 'react-native';
import { connect, useSelector } from 'react-redux';

import ColorWrapper from '@styles/colors';
import { GenerateImageURL } from '@interfaces/methods';
import { ColorFunction } from '@styles/colors/types';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { OP_DOCUMENTS_DISPLAY_MODE, scheduledProductionOperationIdParam } from '@components/operatorPanel/constants/operatorPanel.constants';
import { ScrollView } from 'react-native-gesture-handler';
import { useBuildImageContextForAdding } from '@components/operatorPanel/hooks/useBuildImageContextForAdding';
import { KEYS_TAGS, TAG_KEYS } from '@components/imageHandling/common';
import TopPanel from '@components/operatorPanel/components/OperatorPanelInformation/TopPanel';
import OperatorPanelInformationAllDocumentsActions from '@components/operatorPanel/components/OperatorPanelInformation/components/OperatorPanelInformationAllDocumentsScreen/OperatorPanelInformationAllDocumentsActions';
import { scale as initialScale } from '@components/frame';
import Loading from '@components/loading';
import ImagesDocuments from '@components/search/images';
import { font as initialFont } from '@styles/fonts';
import { searchDisplayMode } from '@helpers';
import OperatorPanelInformationDocuments, { NoResult } from './OperatorPanelInformationDocuments';
import { OPTabInReducer, ScaleFunction } from '@components/operatorPanel/interfaces';
import { useTabCount } from '@v2/pages/common/hooks/useTabCount';
import { DocType, ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';
import { selectCurrentContextLoading, selectCurrentImageDocumentContext } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { generateImageURL } from '@reducers/blobStorage';

interface OperatorPanelInformationDocumentsDifferentModesProps {
  images: ImageDocumentStoreItem[];
  noResult: boolean;
  scopeIdsForDefaultLinking: (keyof typeof TAG_KEYS)[];
  C: ColorFunction;
  documentTypeForDefaultLinking?: DocType;
  withAttachToBlock?: boolean;
  title: string;
  tabs: OPTabInReducer[];
  withBackButton?: boolean;
  shouldHideDownloadButton?: boolean;
  shouldHideThumbnailButton?: boolean;
  shouldHideAttachButton?: boolean;
  shouldShowAddButtonIfSingleImage?: boolean;
  shouldDisplayModeIcon?: boolean;
  scale?: ScaleFunction;
  font?: typeof initialFont;
  imageBg?: string;
  generateImageURL: GenerateImageURL;
  testID?: string;
}

const OperatorPanelInformationDocumentsDifferentModes: FC<OperatorPanelInformationDocumentsDifferentModesProps> = ({
  images,
  noResult,
  documentTypeForDefaultLinking,
  scopeIdsForDefaultLinking,
  title,
  C,
  tabs,
  imageBg,
  generateImageURL,
  withAttachToBlock = false,
  withBackButton = true,
  shouldDisplayModeIcon = true,
  shouldHideDownloadButton = true,
  shouldHideAttachButton = true,
  shouldShowAddButtonIfSingleImage = true,
  shouldHideThumbnailButton = true,
  font = initialFont,
  scale = initialScale,
  testID,
}) => {
  const route = useRoute();
  const navigation = useNavigation<any>();

  const imagesDocumentsLoading = useSelector(selectCurrentContextLoading);

  const context = useSelector(selectCurrentImageDocumentContext);

  const [containerMeasurements, setContainerMeasurements] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  const mode = route.params?.mode;
  const isTileMode = mode !== OP_DOCUMENTS_DISPLAY_MODE.GALLERY;

  const theme = C.scheme === 'light' ? 'Light' : 'Dark';
  const bg = C(`Background/${theme}/System [mobile]`, false);
  const borderColor = C(`Hairline/${theme}/Transpar [mobile]`, false);
  const noResultBg = C(`Background/${theme}/Panel [mobile]`, false);

  const yScrollPosition = useRef<number>(0);
  const scrollViewRef = useRef<ScrollView | null>(null);

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    yScrollPosition.current = event.nativeEvent.contentOffset.y;
  };

  useFocusEffect(
    useCallback(() => {
      if (yScrollPosition.current !== 0 && scrollViewRef.current) {
        scrollViewRef.current?.scrollTo({
          y: yScrollPosition.current,
          animated: false,
        });
      }
    }, [mode])
  );

  useFocusEffect(
    useCallback(() => {
      const multipleImages = images.length > 1;

      if (images.length === 0) return;

      if (mode == null) {
        navigation.setParams({ mode: multipleImages ? OP_DOCUMENTS_DISPLAY_MODE.TILE : OP_DOCUMENTS_DISPLAY_MODE.GALLERY });
        return;
      }

      if (mode === OP_DOCUMENTS_DISPLAY_MODE.TILE && !multipleImages && !imagesDocumentsLoading) {
        navigation.setParams({ mode: OP_DOCUMENTS_DISPLAY_MODE.GALLERY });
      }
    }, [images.length, imagesDocumentsLoading])
  );

  const onImagePress = ({ contentHash }: { documentId: number; showActions: boolean; contentHash: string }) => {
    navigation.setParams({ selectedImageKey: contentHash, mode: OP_DOCUMENTS_DISPLAY_MODE.GALLERY, backButton: withBackButton });
  };

  const onBackButtonPress = () => {
    navigation.setParams({ selectedImageKey: undefined, mode: OP_DOCUMENTS_DISPLAY_MODE.TILE, backButton: undefined });
  };

  const PADDING = 18;

  const selectedJobId = route.params?.[scheduledProductionOperationIdParam];
  const isMachinePerspective = selectedJobId == null;

  const defaultLinking = isMachinePerspective ? [KEYS_TAGS.ProductionBatch] : [KEYS_TAGS.ProductionOperation];
  const links = [...new Set([...scopeIdsForDefaultLinking, ...defaultLinking])];

  const imageLinksForAdding = useBuildImageContextForAdding(selectedJobId, links as (keyof typeof TAG_KEYS)[]);

  const { onSuccessImageUpload } = useTabCount();

  const imageContextForAdding = {
    tags: imageLinksForAdding,
    ...(documentTypeForDefaultLinking ? { documentType: documentTypeForDefaultLinking } : {}),
  };

  const isMeasurementsValid = containerMeasurements.width > 0 && containerMeasurements.height > 0;
  const shouldDisplayLoader = imagesDocumentsLoading;
  const shouldDisplayNoResult = !shouldDisplayLoader && noResult && isMeasurementsValid;
  const shouldDisplayData = !shouldDisplayLoader && !shouldDisplayNoResult && isMeasurementsValid;

  if (isTileMode) {
    return (
      <View
        testID={testID}
        onLayout={(event) => {
          const { height, width } = event.nativeEvent.layout;
          setContainerMeasurements({ width, height });
        }}
        style={{ flex: 1, backgroundColor: images.length === 0 || imagesDocumentsLoading ? noResultBg : 'transparent' }}
      >
        <View style={{ borderBottomColor: borderColor, borderBottomWidth: StyleSheet.hairlineWidth }}>
          <TopPanel title={title}>
            <OperatorPanelInformationAllDocumentsActions
              scale={scale}
              imageContextForAdding={imageContextForAdding}
              amountOfImages={images.length ?? 0}
              onSuccessImageUpload={onSuccessImageUpload}
            />
          </TopPanel>
        </View>
        {shouldDisplayLoader && (
          <View style={{ marginTop: scale(40) }}>
            <Loading background={noResultBg} />
          </View>
        )}
        {shouldDisplayNoResult && (
          <View
            style={[
              { flex: 1, backgroundColor: images.length === 0 ? noResultBg : bg },
              images.length === 0 && {
                justifyContent: 'center',
              },
            ]}
          >
            <NoResult {...{ C, scale, font, tabs, imageContextForAdding, onSuccessImageUpload }} />
          </View>
        )}
        {shouldDisplayData && (
          <View style={{ backgroundColor: bg, flex: 1, width: '100%', paddingHorizontal: scale(PADDING), paddingVertical: scale(PADDING) }}>
            <ScrollView
              ref={scrollViewRef}
              onScroll={handleScroll}
              scrollEventThrottle={16}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ paddingTop: scale(5) }}
            >
              <ImagesDocuments
                {...{
                  scale,
                  font,
                  withoutLoadingIndicator: true,
                  withoutVerticalCentered: true,
                  containerWidth: containerMeasurements.width - scale(PADDING) * 2,
                  withoutHorizontalPaddings: true,
                  totalResultCount: images.length,
                  searchResult: images,
                  isError: false,
                  loaded: true,
                  C,
                  isSmallMode: false,
                  isLineMode: false,
                  isInfoTilesMode: false,
                  isTableMode: false,
                  displayMode: searchDisplayMode.block,
                  fetchData: () => {},
                  generateImageURL,
                  recentlyViewed: [],
                  setRecentlyViewed: () => {},
                  facets: null,
                  onImagePress,
                }}
              />
            </ScrollView>
          </View>
        )}
      </View>
    );
  }

  const hasMultipleImages = images.length > 1;
  const shouldDisplayBackButton = withBackButton && hasMultipleImages;

  return (
    <OperatorPanelInformationDocuments
      scale={scale}
      font={font}
      noResult={noResult}
      images={images}
      shouldDisplayModeIcon={shouldDisplayModeIcon && images.length > 1}
      shouldHideDownloadButton={shouldHideDownloadButton}
      shouldHideAttachButton={shouldHideAttachButton}
      shouldShowAddButtonIfSingleImage={shouldShowAddButtonIfSingleImage}
      shouldHideThumbnailButton={shouldHideThumbnailButton}
      withAttachToBlock={!!withAttachToBlock}
      title={title}
      withBackButton={shouldDisplayBackButton}
      imageContextForAdding={imageContextForAdding}
      onBackButtonPress={shouldDisplayBackButton ? onBackButtonPress : () => {}}
      testID={testID}
    />
  );
};

export default connect(({ operatorPanel: { tabs } }) => ({ tabs }), { generateImageURL })(
  ColorWrapper(OperatorPanelInformationDocumentsDifferentModes)
);
